"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Bot, Phone, Mail, Globe, MapPin, ChevronDown, X,
  MessageCircle, PhoneCall, ClipboardList,
  XCircle, Video,
  BookOpen, Briefcase, Building2, GraduationCap,
  Wrench, FileText, UserPlus, MessagesSquare, AtSign,
} from "lucide-react";

import type { AgentSidebarOpenDetail } from "~/lib/marketplace/site-agent-sidebar";
import { reportAgentProfileEmbedHeight, openProductionEasyGptWidget } from "~/lib/marketplace/agent-profile-embed";
import { isCallMePhoneProvisioned, isSmsProvisioned } from "~/lib/marketplace/agent-phone-provisioned";
import {
  isEmailInboxProvisioned,
  resolveDisplayAgentEmail,
} from "~/lib/marketplace/agent-email-provisioned";
import { buildAgentResponsibilities } from "~/lib/agent/agent-responsibilities";
import { BuilderWjsPreview } from "~/components/builder/builder-wjs-preview";

// ── Constants ───────────────────────────────────────────────────────────────
const NAVY = "#1a2332";
// P is set as a CSS variable (--cv2-primary) on the root container at render time
// so all sub-components reference it via "var(--cv2-primary)"
const P = "var(--cv2-primary)";
const P_TINT = "color-mix(in srgb, var(--cv2-primary) 10%, transparent)";

// ── Type helpers ────────────────────────────────────────────────────────────
interface TeamMemberShape {
  name: string;
  role?: string;
  imageUrl?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  isLead?: boolean;
}

interface LocationShape {
  name: string;
  isDefault?: boolean;
  address: string;
  phone: string;
  hours: string;
  leadMember?: TeamMemberShape | null;
  otherMembers?: TeamMemberShape[];
}

interface McpToolShape {
  name: string;
  icon?: string;
  description?: string;
  tools?: string[];
  status?: "active" | "inactive";
}

interface KnowledgeSectionShape { heading: string; content: string; }
interface KnowledgeDocShape { title: string; excerpt: string; addedAt: string; }
interface ConversationShape { name: string; phone: string | null; messages: number; startedAt: string; status: "completed" | "abandoned" | "started"; }
interface RecentConvoChannel { channel: string; icon: string; conversations: ConversationShape[]; }

// ── Sub-components (defined outside main — required for stable hook state) ──

function SectionHeading({ title }: { title: string }) {
  return (
    <div className="mb-3">
      <p className="text-[10px] font-bold tracking-widest uppercase text-white/60">{title}</p>
      <div className="h-px bg-white/20 mt-1.5" />
    </div>
  );
}

function MainSectionHeading({ title, icon: Icon }: { title: string; icon?: React.ComponentType<any> }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: P }} />}
      <div>
        <h2 className="text-sm font-black uppercase tracking-widest" style={{ color: NAVY }}>{title}</h2>
        <div className="h-0.5 rounded-full mt-1 w-8" style={{ backgroundColor: P }} />
      </div>
    </div>
  );
}

function MemberRow({ member, isLead }: { member: TeamMemberShape; isLead?: boolean }) {
  const [open, setOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const initials = member.name
    .split(" ")
    .map((n) => n[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const hasDetails = member.phone || member.email || member.website || member.address;
  const hasProfileImage = !!member.imageUrl && !imageError;

  return (
    <div className="py-2 border-b border-[#1a2332]/8 last:border-0">
      <button onClick={() => hasDetails && setOpen(!open)} className="w-full flex items-center gap-2.5 text-left">
        {hasProfileImage ? (
          <img
            src={member.imageUrl}
            alt={member.name}
            className="w-7 h-7 rounded-full object-cover shrink-0"
            onError={() => setImageError(true)}
          />
        ) : (
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
            style={isLead ? { background: P_TINT, color: P } : { background: "#f3f4f6", color: "#6b7280" }}
          >
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold truncate" style={{ color: NAVY }}>{member.name}</span>
            {isLead && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: P_TINT, color: P }}>
                Lead
              </span>
            )}
          </div>
          {member.role && <p className="text-[10px] text-gray-400 truncate">{member.role}</p>}
        </div>
        {hasDetails && (
          <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>
      {open && hasDetails && (
        <div className="mt-2 pl-9 space-y-1">
          {member.phone && (
            <a href={`tel:${member.phone}`} className="flex items-center gap-1.5 text-[10px] text-gray-500 hover:text-gray-700">
              <Phone className="w-3 h-3" />{member.phone}
            </a>
          )}
          {member.email && (
            <a href={`mailto:${member.email}`} className="flex items-center gap-1.5 text-[10px] text-gray-500 hover:text-gray-700">
              <Mail className="w-3 h-3" />{member.email}
            </a>
          )}
          {member.website && (
            <a href={member.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] text-gray-500 hover:text-gray-700">
              <Globe className="w-3 h-3" />{member.website}
            </a>
          )}
          {member.address && (
            <p className="flex items-start gap-1.5 text-[10px] text-gray-500">
              <MapPin className="w-3 h-3 mt-0.5 shrink-0" />{member.address}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="rounded-lg p-2.5 bg-white/5">
      <div className="text-base leading-none mb-1">{icon}</div>
      <div className="text-sm font-bold text-white">{value}</div>
      <div className="text-[9px] text-white/50 mt-0.5 leading-tight">{label}</div>
    </div>
  );
}

interface ResponsibilityCard {
  key: string;
  title: string;
  description: string;
  enabled: boolean;
}

function ExperienceCard({ role }: { role: ResponsibilityCard }) {
  return (
    <div className="rounded-md border border-[#1a2332]/10 p-4 bg-white hover:shadow-sm transition-all">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-bold leading-snug" style={{ color: NAVY }}>{role.title}</h4>
        <span
          className="shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
          style={role.enabled
            ? { background: "#dcfce7", color: "#15803d" }
            : { background: "#fef2f2", color: "#b91c1c" }}
        >
          {role.enabled ? "Enabled" : "Disabled"}
        </span>
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">{role.description}</p>
    </div>
  );
}

function McpCard({ tool }: { tool: McpToolShape }) {
  const active = tool.status !== "inactive";
  return (
    <div className="rounded-md border border-[#1a2332]/10 overflow-hidden bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#1a2332]/8 bg-[#1a2332]/2">
        <span className="text-base">{tool.icon ?? "🔧"}</span>
        <span className="text-xs font-semibold flex-1" style={{ color: NAVY }}>{tool.name}</span>
        <span
          className="text-[9px] font-bold px-2 py-0.5 rounded-full"
          style={active ? { background: "#dcfce7", color: "#15803d" } : { background: "#f3f4f6", color: "#9ca3af" }}
        >
          {active ? "Connected" : "Inactive"}
        </span>
      </div>
      <div className="px-4 py-3">
        {tool.description && <p className="text-xs text-gray-500 mb-2">{tool.description}</p>}
        {tool.tools && tool.tools.length > 0 && (
          <ul className="space-y-1">
            {tool.tools.map((t, i) => {
              const colonIdx = t.indexOf(":");
              const name = colonIdx !== -1 ? t.slice(0, colonIdx) : t;
              const desc = colonIdx !== -1 ? t.slice(colonIdx + 1).trim() : "";
              return (
                <li key={i} className="text-[10px] text-gray-500 flex items-start gap-1">
                  <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: P }} />
                  <span><span style={{ color: P }}>{name}</span>{desc ? `: ${desc}` : ""}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ConversationShape["status"] }) {
  if (status === "completed") return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-50 text-green-600">completed</span>;
  if (status === "abandoned") return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-500">abandoned</span>;
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">started</span>;
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Main component ───────────────────────────────────────────────────────────

interface Props {
  projectId: string;
  openHireDialog?: boolean;
  embedBuilder?: boolean;
  data: {
    project: any;
    cv2Assistants?: Array<{
      id: string;
      phoneNumber: string | null;
      phoneProvider: string | null;
      agentPhoneAgentId: string | null;
      agentPhoneNumberId: string | null;
      isProvisioned: boolean;
      vapiAiPhoneId: string | null;
      assistantId: string | null;
    }>;
    recentChats: any[];
    stats: {
      totalChats: number;
      completedChats: number;
      abandonedChats: number;
      handoffChats: number;
      conversionRate: number;
      contactCount: number;
      newContactCount: number;
      existingContactCount: number;
      avgMessages: number;
    };
  };
}

export default function AgentProfileClient({
  projectId,
  data,
  openHireDialog = false,
  embedBuilder = false,
}: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const widgetReloadKey = embedBuilder
    ? Number.parseInt(searchParams.get("refresh") ?? "0", 10) || 0
    : 0;
  // ── All hooks BEFORE any early return ──────────────────────────────────
  const [isMounted, setIsMounted] = useState(false);
  const [notEnabledOpen, setNotEnabledOpen] = useState(false);
  const [trainingOpen, setTrainingOpen] = useState<Record<string, boolean>>({});
  const [convoOpen, setConvoOpen] = useState<Record<string, boolean>>({});

  useEffect(() => { setIsMounted(true); }, []);

  // Persist Direct Mail attribution for downstream conversation/lead events
  useEffect(() => {
    if (searchParams.get("sg_dm") !== "1") return;
    try {
      sessionStorage.setItem(
        "sg_dm_attribution",
        JSON.stringify({
          campaignId: searchParams.get("campaign_id"),
          prospectId: searchParams.get("prospect_id"),
          mailPieceId: searchParams.get("mail_piece_id"),
          aiEmployeeId: searchParams.get("ai_employee_id") || projectId,
        }),
      );
    } catch {
      // ignore
    }
  }, [searchParams, projectId]);

  const isProfileEmbed = searchParams.get("embed") === "1";

  const tabParam = searchParams.get("tab");
  const profileChannelTab = useMemo((): AgentSidebarOpenDetail["tab"] | null => {
    if (!tabParam) return null;
    const allowed: AgentSidebarOpenDetail["tab"][] = [
      "aiChat",
      "call",
      "talk",
      "videoCall",
      "contact",
      "sms",
      "email",
    ];
    return allowed.includes(tabParam as AgentSidebarOpenDetail["tab"])
      ? (tabParam as AgentSidebarOpenDetail["tab"])
      : null;
  }, [tabParam]);

  useEffect(() => {
    if (!embedBuilder) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;
    html.style.height = "100%";
    html.style.overflow = "hidden";
    body.style.height = "100%";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      html.style.height = prevHtmlHeight;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, [embedBuilder]);

  useEffect(() => {
    if (!embedBuilder || !isMounted) return;

    const report = () => reportAgentProfileEmbedHeight(projectId);
    report();

    const observer = new ResizeObserver(() => report());
    observer.observe(document.documentElement);
    observer.observe(document.body);

    window.addEventListener("load", report);
    const delayed = window.setTimeout(report, 400);

    return () => {
      observer.disconnect();
      window.removeEventListener("load", report);
      window.clearTimeout(delayed);
    };
  }, [embedBuilder, isMounted, projectId, data]);

  useEffect(() => {
    if (openHireDialog && !embedBuilder) {
      router.push(`/agents/${projectId}/hire`);
    }
  }, [openHireDialog, embedBuilder, projectId, router]);

  const openWidget = useCallback((initialTab?: AgentSidebarOpenDetail["tab"]) => {
    openProductionEasyGptWidget(initialTab || "aiChat");
  }, []);

  useEffect(() => {
    if (!isMounted || !profileChannelTab) return;
    const timer = window.setTimeout(() => openWidget(profileChannelTab), 600);
    return () => window.clearTimeout(timer);
  }, [isMounted, profileChannelTab, openWidget]);

  // ── Derive display data ─────────────────────────────────────────────────
  const { project, cv2Assistants = [], recentChats, stats } = data;
  const agent = project.aiAgent ?? {};
  const visualSettings = project.visualSettings ?? {};
  const channels = (visualSettings.channels ?? {}) as Record<string, boolean>;
  const projectSettings = (project.projectSettings ?? [])[0] as any;

  // Dynamic brand colors from project visual settings
  const primaryColor: string = visualSettings.headerColor || "#1d6fe8";
  const brandingEnabled: boolean = visualSettings.customBrandingEnabled === true;
  const poweredByName: string = visualSettings.poweredByCompanyName ?? "";
  const poweredByUrl: string = visualSettings.poweredByCompanyUrl ?? "";

  const agentName: string = agent.name ?? project.title ?? "AI Assistant";
  const agentRole: string = agent.jobTitle ?? "AI Patient Coordinator";
  const marketplaceListingType: string = project.listingType ?? "NONE";
  const isMarketplaceListed =
    marketplaceListingType === "OFFICIAL" || marketplaceListingType === "MOONLIGHT";
  const agentBio: string = agent.persona ?? "";
  const agentPhoto: string | null = agent.base64Avatar ?? null;
  const companyName: string = project.title ?? "";
  const companyWebsite: string = project.websiteUrl ?? "";
  const companyDescription: string = projectSettings?.businessDescription ?? "";
  const agentEmail: string =
    resolveDisplayAgentEmail(project.notificationSettings) ??
    agent.emailAddress ??
    "";
  const phoneAgent =
    project.assistant ??
    (cv2Assistants ?? []).find(
      (a) =>
        String(a?.phoneProvider ?? "").toLowerCase() === "agentphone" ||
        !!a?.agentPhoneAgentId,
    ) ??
    (cv2Assistants ?? [])[0] ??
    null;
  const callMePhoneProvisioned = isCallMePhoneProvisioned(phoneAgent);
  const smsPhoneProvisioned = isSmsProvisioned(phoneAgent);
  const emailInboxProvisioned = isEmailInboxProvisioned(project.notificationSettings);
  const agentPhone: string = callMePhoneProvisioned
    ? (phoneAgent?.phoneNumber?.trim() ?? "")
    : "";
  const agentWebsite: string = agent.websiteUrl ?? project.websiteUrl ?? "";

  const channelFlags = {
    liveChat: channels.aiChat === true,
    phone: channels.call === true,
    contact: channels.contact === true,
    browserCall: channels.talk === true,
    sms: channels.sms === true,
    email: channels.email === true,
    whatsapp: channels.whatsapp === true,
    messenger: channels.messenger === true,
  };
  const showCallMe = channelFlags.phone && callMePhoneProvisioned;
  const showCallNow = showCallMe && !!agentPhone;
  const showTextUs = channelFlags.sms && smsPhoneProvisioned;
  const showEmailMe = channelFlags.email && emailInboxProvisioned;
  const showVideoCall =
    channelFlags.browserCall && visualSettings.talkAnamVideoEnabled === true;

  const responsibilities: ResponsibilityCard[] = buildAgentResponsibilities(channels).map(
    (role) => ({
      ...role,
      enabled:
        role.key === "call"
          ? channelFlags.phone && callMePhoneProvisioned
          : role.key === "sms"
            ? channelFlags.sms && smsPhoneProvisioned
            : role.key === "email"
              ? channelFlags.email && emailInboxProvisioned
              : role.enabled,
    }),
  );

  const schedulingEnabled = (project.mcpConfigurations ?? []).some(
    (m: any) => (m.serverId?.startsWith("juvonno") || m.serverId?.startsWith("nexhealth")) && m.enabled,
  );

  const ALL_CHANNELS = [
    { key: "liveChat", label: "Live Chat" },
    { key: "phone", label: "Phone Answering" },
    { key: "contact", label: "Contact Management" },
    { key: "browserCall", label: "Browser Calls" },
    { key: "sms", label: "Text Messages" },
    { key: "email", label: "Email Support" },
    { key: "scheduling", label: "Appointment Scheduling" },
  ] as const;

  const isChannelEnabled = (key: (typeof ALL_CHANNELS)[number]["key"]) => {
    if (key === "scheduling") return schedulingEnabled;
    if (key === "phone") return channelFlags.phone && callMePhoneProvisioned;
    if (key === "sms") return channelFlags.sms && smsPhoneProvisioned;
    if (key === "email") return channelFlags.email && emailInboxProvisioned;
    return channelFlags[key as keyof typeof channelFlags];
  };
  const enabledChannels = ALL_CHANNELS.filter((c) => isChannelEnabled(c.key));
  const disabledChannels = ALL_CHANNELS.filter((c) => !isChannelEnabled(c.key));

  // MCP tools
  let mcpTools: McpToolShape[] = [];
  try { mcpTools = (project.mcpConfigurations ?? []).map((m: any) => {
    const META: Record<string, { name: string; icon: string; description: string }> = {
      juvonno: { name: "Juvonno EMR", icon: "🏥", description: "Patient lookup, appointment scheduling, and clinical data management." },
      nexhealth: { name: "NexHealth EMR", icon: "🦷", description: "Patient onboarding, scheduling, and engagement tools." },
      "vapi-basic-operations": { name: "Phone Operations", icon: "📞", description: "Business hours, call transfers, knowledge search, and team handoffs." },
      phone_core: { name: "Phone Core", icon: "📱", description: "Core phone agent tools — time checks, team handoffs, and transfers." },
    };
    const key = Object.keys(META).find((k) => (m.serverId ?? "").toLowerCase().startsWith(k));
    const meta = key ? META[key]! : { name: m.serverId, icon: "🔧", description: "Custom integration." };
    const cached = (m.cachedData ?? {}) as Record<string, any>;
    const toolNames: string[] = Array.isArray(cached.tools)
      ? cached.tools.map((t: any) => String(t.name ?? t))
      : [];
    return { name: m.serverName || meta.name, icon: meta.icon, description: meta.description, status: m.enabled ? "active" : "inactive", tools: toolNames };
  }); } catch (_) {}

  // Team Handoff Actions — from CtaCluster settings
  let teamHandoffTool: McpToolShape | null = null;
  try {
    const settings = Array.isArray(project.projectSettings)
      ? project.projectSettings[0]
      : project.projectSettings;
    const clusters: any[] = settings?.ctaClusters ?? [];
    if (clusters.length > 0) {
      teamHandoffTool = {
        name: "Team Handoff Actions",
        icon: "🤝",
        description: clusters.map((c: any) => c.ctaQuestion).filter(Boolean).join(" · "),
        status: "active",
        tools: clusters.map((c: any) => c.ctaQuestion).filter(Boolean),
      };
    }
  } catch (_) {}

  // Locations
  let locations: LocationShape[] = [];
  try {
    locations = (project.projectLocation ?? []).map((loc: any) => {
      const members: TeamMemberShape[] = (loc.teamMemberLocations ?? [])
        .map((tml: any) => {
          const tm = tml.teamMember ?? {};
          const collab = tm.collaborator ?? {};
          let name = (tm.name ?? "").trim();
          if (!name || name.includes("@")) {
            const fn = (collab.firstName ?? "").trim();
            const ln = (collab.lastName ?? "").trim();
            name = [fn, ln].filter(Boolean).join(" ").trim();
          }
          if (!name) {
            const em = (tm.email ?? "").trim();
            if (em) name = em.includes("@") ? (em.split("@")[0] ?? em) : em;
          }
          if (!name) return null;
          return {
            name,
            role: (tm.title ?? tm.collaborator?.title ?? "").trim(),
            imageUrl: (tm.imageUrl ?? tm.collaborator?.imageUrl ?? "").trim() || undefined,
            phone: (tm.phone ?? tm.collaborator?.phone ?? "").trim(),
            email: (tm.email ?? "").trim(),
            website: (tm.collaborator?.websiteUrl ?? "").trim(),
            address: "",
            isLead: tml.isSpecialist === true,
          } as TeamMemberShape;
        })
        .filter(Boolean) as TeamMemberShape[];

      // Fallback: if no team members from TeamMemberLocation, use responsiblePerson* fields
      if (members.length === 0) {
        const firstName = (loc.responsiblePersonFirstName ?? "").trim();
        const lastName = (loc.responsiblePersonLastName ?? "").trim();
        const fullName = [firstName, lastName].filter(Boolean).join(" ");
        if (fullName) {
          members.push({
            name: fullName,
            role: (loc.responsiblePersonTitle ?? "").trim(),
            phone: (loc.responsiblePersonPhone ?? "").trim(),
            isLead: true,
          });
        }
      }

      // Determine lead by index (avoids reference comparison bugs)
      const leadIdx = members.findIndex((m) => m.isLead);
      const effectiveLeadIdx = leadIdx !== -1 ? leadIdx : members.length > 0 ? 0 : -1;
      const lead = effectiveLeadIdx !== -1 ? { ...members[effectiveLeadIdx]!, isLead: true } : null;
      const others = effectiveLeadIdx !== -1 ? members.filter((_, i) => i !== effectiveLeadIdx) : [];

      const parts = [loc.address, loc.city, loc.state, loc.zipCode].filter(Boolean);
      return {
        name: loc.name ?? "",
        isDefault: loc.isDefaultLocation ?? false,
        address: parts.join(", "),
        phone: loc.locationPhone ?? "",
        hours: loc.businessHours ?? "",
        leadMember: lead,
        otherMembers: others,
      };
    });
  } catch (err) {
    console.error("[CV2] locations mapping error:", err);
  }

  // Extended knowledge
  let extendedKnowledge: KnowledgeSectionShape[] = [];
  try {
    const raw: string = projectSettings?.extendedBusinessDescription ?? "";
    if (raw.trim()) {
      const sections = raw.split(/\n#{1,3}\s/).filter(Boolean);
      extendedKnowledge = sections.map((s) => {
        const nl = s.indexOf("\n");
        return nl === -1
          ? { heading: s.trim(), content: "" }
          : { heading: s.slice(0, nl).trim(), content: s.slice(nl).trim() };
      });
      if (extendedKnowledge.length === 0) {
        extendedKnowledge = [{ heading: "Business Overview", content: raw }];
      }
    }
  } catch (_) {}

  const knowledgeDocumentsRaw = ((project.knowledgeBase?.documents ?? []) as any[]);
  let knowledgeDocs: KnowledgeDocShape[] = [];
  try {
    knowledgeDocs = knowledgeDocumentsRaw.map((d) => ({
      title: d.title ?? d.name ?? "Document",
      excerpt: ((d.content ?? d.text ?? "") as string).slice(0, 200),
      addedAt: d.createdAt,
    }));
  } catch (_) {}

  const websiteCount = knowledgeDocumentsRaw.filter((doc: any) =>
    doc.sourceType === "web" || doc.sourceType === "web_scraping",
  ).length;
  const filesCount = knowledgeDocumentsRaw.filter((doc: any) =>
    doc.sourceType === "file" || doc.sourceType === "file_upload",
  ).length;
  const textCount = knowledgeDocumentsRaw.filter((doc: any) =>
    doc.sourceType === "text",
  ).length;
  const qaCount = knowledgeDocumentsRaw.filter((doc: any) =>
    doc.sourceType === "qa" || doc.sourceType === "faq_scraping",
  ).length;
  const videoCount = knowledgeDocumentsRaw.filter((doc: any) =>
    doc.sourceType === "video" || doc.sourceType === "video_upload" || doc.sourceType === "youtube",
  ).length;
  const audioCount = knowledgeDocumentsRaw.filter((doc: any) =>
    doc.sourceType === "audio" || doc.sourceType === "audio_upload",
  ).length;

  const companyProfileConfigured = !!(projectSettings?.extendedBusinessDescription ?? "").trim();
  const icpConfigured = !!(
    (projectSettings?.businessDescription ?? "").trim() ||
    (projectSettings?.idealCustomerDescription ?? "").trim() ||
    (projectSettings?.leadScoringAlgorithm ?? "").trim() ||
    (projectSettings?.sicCode ?? "").trim()
  );

  const basicTrainingItems = [
    { key: "company-profile", label: "Company Profile", meta: companyProfileConfigured ? "Configured" : "Not set" },
    { key: "icp", label: "ICP", meta: icpConfigured ? "Configured" : "Not set" },
  ];

  const trainingDocsByType = {
    "rag-website": knowledgeDocumentsRaw.filter((doc: any) => doc.sourceType === "web" || doc.sourceType === "web_scraping"),
    "rag-files": knowledgeDocumentsRaw.filter((doc: any) => doc.sourceType === "file" || doc.sourceType === "file_upload"),
    "rag-text": knowledgeDocumentsRaw.filter((doc: any) => doc.sourceType === "text"),
    "rag-qa": knowledgeDocumentsRaw.filter((doc: any) => doc.sourceType === "qa" || doc.sourceType === "faq_scraping"),
    "rag-video": knowledgeDocumentsRaw.filter((doc: any) => doc.sourceType === "video" || doc.sourceType === "video_upload" || doc.sourceType === "youtube"),
    "rag-audio": knowledgeDocumentsRaw.filter((doc: any) => doc.sourceType === "audio" || doc.sourceType === "audio_upload"),
  } as Record<string, any[]>;

  const ragTrainingItems = [
    { key: "rag-website", label: "Website", meta: `${websiteCount}` },
    { key: "rag-files", label: "Files", meta: `${filesCount}` },
    { key: "rag-text", label: "Text", meta: `${textCount}` },
    { key: "rag-qa", label: "Q&A", meta: `${qaCount}` },
    { key: "rag-video", label: "Video", meta: `${videoCount}` },
    { key: "rag-audio", label: "Audio", meta: `${audioCount}` },
  ].filter((item) => (trainingDocsByType[item.key] ?? []).length > 0);

  // Recent conversations grouped by channel
  const channelMap: Record<string, RecentConvoChannel> = {
    PHONE: { channel: "Phone Calls", icon: "📞", conversations: [] },
    CHAT: { channel: "Website Chat", icon: "💬", conversations: [] },
    CONTACT_FORM: { channel: "Contact Form", icon: "📋", conversations: [] },
  };
  (recentChats ?? []).forEach((chat: any) => {
    const key: string = chat.contactType ?? "CHAT";
    const group = channelMap[key] ?? channelMap["CHAT"]!;
    if (group.conversations.length >= 5) return;
    const status: ConversationShape["status"] =
      chat.state === "COMPLETED" ? "completed" : chat.state === "ABANDONED" ? "abandoned" : "started";
    group.conversations.push({
      name: chat.placeholderName ?? "Unknown Visitor",
      phone: chat.capturedPhoneNumber ?? null,
      messages: (chat._count as any)?.chatMessages ?? 0,
      startedAt: chat.createdAt,
      status,
    });
  });
  const recentConvoGroups = Object.values(channelMap).filter((g) => g.conversations.length > 0);

  const hasTraining = basicTrainingItems.length > 0 || ragTrainingItems.length > 0;
  const hasMcp = mcpTools.length > 0 || teamHandoffTool !== null;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        :root { --cv2-primary: ${primaryColor}; }
        html { scroll-behavior: smooth; }
        .cv2-scrollbar::-webkit-scrollbar { width: 4px; }
        .cv2-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .cv2-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 4px; }
      `}</style>

      <div
        className={
          embedBuilder
            ? "flex h-full min-h-0 flex-col overflow-hidden bg-white"
            : "flex min-h-screen bg-white"
        }
      >

        {/* ─── SIDEBAR (hidden in hire/builder embed — stats don't apply to new preview agents) ─ */}
        {!embedBuilder ? (
        <aside
          className="hidden md:flex flex-col w-72 shrink-0 fixed top-0 left-0 bottom-0 overflow-y-auto cv2-scrollbar"
          style={{ backgroundColor: NAVY }}
        >
          <div className="flex flex-col gap-5 p-6">

            {/* Profile photo + name + role */}
            <div className="flex flex-col items-center text-center pt-2">
              <div className="relative mb-3 inline-flex">
                {agentPhoto ? (
                  <img
                    src={agentPhoto}
                    alt={agentName}
                    className="w-24 h-24 rounded-full object-cover border-2 border-white"
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-full border-2 border-white flex items-center justify-center"
                    style={{ background: P_TINT }}
                  >
                    <Bot className="w-10 h-10" style={{ color: P }} />
                  </div>
                )}
                <span
                  className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-400 shadow-sm"
                  title="Online"
                  aria-hidden
                />
              </div>
              <p className="font-bold text-white text-base leading-tight">{agentName}</p>
              <p className="text-xs mt-0.5" style={{ color: P }}>{agentRole}</p>
            </div>

            {/* Contact info */}
            {(agentPhone || agentEmail || agentWebsite) && (
              <div>
                <SectionHeading title="Contact" />
                <div className="space-y-2">
                  {agentPhone && (
                    <a href={`tel:${agentPhone}`} className="flex items-center gap-2 text-white/70 hover:text-white text-xs transition-colors">
                      <Phone className="w-3.5 h-3.5 shrink-0" style={{ color: P }} />{agentPhone}
                    </a>
                  )}
                  {agentEmail && (
                    <a href={`mailto:${agentEmail}`} className="flex items-center gap-2 text-white/70 hover:text-white text-xs transition-colors">
                      <Mail className="w-3.5 h-3.5 shrink-0" style={{ color: P }} />{agentEmail}
                    </a>
                  )}
                  {agentWebsite && (
                    <a href={agentWebsite} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/70 hover:text-white text-xs transition-colors">
                      <Globe className="w-3.5 h-3.5 shrink-0" style={{ color: P }} />{agentWebsite.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Key Capabilities */}
            <div>
              <SectionHeading title="Key Capabilities" />
              <div className="space-y-1.5">
                {enabledChannels.map((c) => (
                  <div key={c.key} className="flex items-center gap-2 text-[10px] font-medium text-white">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                    {c.label}
                  </div>
                ))}
              </div>

              {/* Not Enabled — collapsible */}
              {disabledChannels.length > 0 && (
                <div className="mt-3">
                  <button
                    onClick={() => setNotEnabledOpen(!notEnabledOpen)}
                    className="flex items-center gap-1 text-[9px] text-white/40 hover:text-white/60 transition-colors"
                  >
                    <ChevronDown className={`w-3 h-3 transition-transform ${notEnabledOpen ? "rotate-180" : ""}`} />
                    Not Enabled ({disabledChannels.length})
                  </button>
                  {notEnabledOpen && (
                    <div className="space-y-1.5 mt-2">
                      {disabledChannels.map((c) => (
                        <div key={c.key} className="flex items-center gap-2 text-[10px] text-white/30">
                          <XCircle className="w-2.5 h-2.5 shrink-0" />
                          {c.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Recent Conversations */}
            {recentConvoGroups.length > 0 && (
              <div>
                <SectionHeading title="Recent Conversations" />
                <div className="space-y-1.5">
                  {recentConvoGroups.map((group) => {
                    const isOpen = convoOpen[group.channel] === true; // default closed
                    return (
                      <div key={group.channel} className="rounded-md overflow-hidden bg-white/5">
                        <button
                          onClick={() => setConvoOpen((p) => ({ ...p, [group.channel]: !isOpen }))}
                          className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/8 transition-colors text-left"
                        >
                          <span className="text-[10px] font-bold text-white/70 uppercase tracking-wider">
                            {group.icon} {group.channel}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] text-white/30">{group.conversations.length}</span>
                            <ChevronDown className={`w-3 h-3 text-white/40 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-2 pb-2 space-y-1.5">
                            {group.conversations.map((conv, i) => (
                              <div key={i} className="flex items-start gap-2 bg-white/5 rounded-md p-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] font-medium text-white truncate">{conv.name}</p>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <StatusBadge status={conv.status} />
                                    <span className="text-[9px] text-white/30">{timeAgo(conv.startedAt)}</span>
                                  </div>
                                </div>
                                <span className="text-[9px] text-white/30 shrink-0">{conv.messages} msg</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recent Activity stats */}
            <div>
              <SectionHeading title="Recent Activity" />
              <div className="grid grid-cols-2 gap-2">
                <StatTile label="Total Conversations" value={String(stats.totalChats)} icon="💬" />
                <StatTile label="Team Handoffs" value={String(stats.handoffChats)} icon="🤝" />
                <StatTile label="Abandoned" value={String(stats.abandonedChats)} icon="⚠️" />
                <StatTile label="Conversion" value={`${stats.conversionRate}%`} icon="📈" />
              </div>
            </div>

            {/* Recent Contacts */}
            <div>
              <SectionHeading title="Recent Contacts" />
              <div className="grid grid-cols-2 gap-2">
                <StatTile label="Total Contacts" value={String(stats.contactCount)} icon="👥" />
                <StatTile label="New (30d)" value={String(stats.newContactCount)} icon="✨" />
                <StatTile label="Existing" value={String(stats.existingContactCount)} icon="🔄" />
                <StatTile label="Avg Messages" value={String(stats.avgMessages)} icon="📨" />
              </div>
            </div>

            {/* My Tools (sidebar — MCP list) */}
            {hasMcp && (
              <div>
                <SectionHeading title="My Tools" />
                <div className="space-y-2">
                  {[...mcpTools, ...(teamHandoffTool ? [teamHandoffTool] : [])].map((tool, i) => (
                    <div key={i} className="rounded-md bg-white/5 overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-2">
                        <span className="text-sm">{tool.icon ?? "🔧"}</span>
                        <span className="text-[10px] text-white/70 flex-1 truncate">{tool.name}</span>
                        <span
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                          style={tool.status !== "inactive"
                            ? { background: "#bbf7d0", color: "#15803d" }
                            : { background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.3)" }}
                        >
                          {tool.status !== "inactive" ? "Connected" : "Inactive"}
                        </span>
                      </div>
                      {tool.tools && tool.tools.length > 0 && (
                        <div className="px-3 pb-2 space-y-0.5">
                          {tool.tools.map((action, j) => (
                            <p key={j} className="text-[9px] text-white/45 truncate pl-5">
                              {action}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="h-4" /> {/* bottom padding */}
          </div>
        </aside>
        ) : null}

        {/* ─── MAIN CONTENT ──────────────────────────────────────────────── */}
        <main
          className={
            embedBuilder
              ? "min-h-0 flex-1 overflow-y-auto cv2-scrollbar"
              : "flex-1 md:ml-72 overflow-y-auto"
          }
        >
          <div className="px-6 py-10 md:px-10 md:py-14">

            {/* Mobile — profile photo beside name (non-embed only; embed uses unified header below) */}
            {!embedBuilder ? (
            <div className="flex items-start gap-4 mb-6 md:hidden">
              {agentPhoto ? (
                <img src={agentPhoto} alt={agentName} className="w-16 h-16 rounded-full object-cover border-2 shrink-0" style={{ borderColor: P }} />
              ) : (
                <div className="w-16 h-16 rounded-full flex items-center justify-center shrink-0" style={{ background: P_TINT }}>
                  <Bot className="w-7 h-7" style={{ color: P }} />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="text-3xl font-black uppercase tracking-tight leading-none" style={{ color: NAVY }}>{agentName}</h1>
                <p className="text-sm font-semibold mt-1" style={{ color: P }}>{agentRole}</p>
              </div>
            </div>
            ) : null}

            {/* Desktop — name + role header (builder embed shows photo beside name) */}
            <div className={embedBuilder ? "flex items-start gap-4 mb-6" : "hidden md:block mb-6"}>
              {embedBuilder ? (
                agentPhoto ? (
                  <img
                    src={agentPhoto}
                    alt={agentName}
                    className="w-16 h-16 rounded-full object-cover border-2 shrink-0"
                    style={{ borderColor: P }}
                  />
                ) : (
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: P_TINT }}
                  >
                    <Bot className="w-7 h-7" style={{ color: P }} />
                  </div>
                )
              ) : null}
              <div className="min-w-0">
                <h1
                  className={
                    embedBuilder
                      ? "text-3xl font-black uppercase tracking-tight leading-none"
                      : "text-5xl font-black uppercase tracking-tight leading-none"
                  }
                  style={{ color: NAVY }}
                >
                  {agentName}
                </h1>
                <p
                  className={embedBuilder ? "text-sm font-semibold mt-1" : "text-lg font-semibold mt-2"}
                  style={{ color: P }}
                >
                  {agentRole}
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 overflow-x-auto pb-1 mb-8 flex-nowrap">
              {channelFlags.liveChat && (
                <button onClick={() => openWidget("aiChat")} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]" style={{ borderColor: NAVY, color: NAVY }}>
                  <MessageCircle className="w-4 h-4" /> Chat
                </button>
              )}
              {channelFlags.browserCall && (
                <button onClick={() => openWidget("talk")} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]" style={{ borderColor: NAVY, color: NAVY }}>
                  <PhoneCall className="w-4 h-4" /> Talk
                </button>
              )}
              {showVideoCall && (
                <button onClick={() => openWidget("videoCall")} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]" style={{ borderColor: NAVY, color: NAVY }}>
                  <Video className="w-4 h-4" /> Video Call
                </button>
              )}
              {channelFlags.contact && (
                <button onClick={() => openWidget("contact")} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]" style={{ borderColor: NAVY, color: NAVY }}>
                  <ClipboardList className="w-4 h-4" /> Contact Us
                </button>
              )}
              {showCallMe && (
                <button onClick={() => openWidget("call")} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]" style={{ borderColor: NAVY, color: NAVY }}>
                  <PhoneCall className="w-4 h-4" /> Call Me
                </button>
              )}
              {showTextUs && (
                <button onClick={() => openWidget("sms")} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]" style={{ borderColor: NAVY, color: NAVY }}>
                  <MessagesSquare className="w-4 h-4" /> Text Me
                </button>
              )}
              {showEmailMe && (
                <button onClick={() => openWidget("email")} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]" style={{ borderColor: NAVY, color: NAVY }}>
                  <AtSign className="w-4 h-4" /> Email Me
                </button>
              )}
              {showCallNow && (
                <a href={`tel:${agentPhone}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border-2 transition-colors hover:text-white hover:bg-[#1a2332]" style={{ borderColor: NAVY, color: NAVY }}>
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              )}
              {isMarketplaceListed && !embedBuilder && (
                <Link
                  href={`/agents/${projectId}/hire`}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: P }}
                >
                  <UserPlus className="w-4 h-4" /> Hire this agent
                </Link>
              )}
            </div>

            {/* Contact bar — email signature style */}
            {(companyName || agentPhone || agentEmail || agentWebsite) && (
              <div className="mb-10 pl-4 border-l-4" style={{ borderColor: P }}>
                {companyName && <p className="font-extrabold text-2xl md:text-3xl leading-tight" style={{ color: NAVY }}>{companyName}</p>}
                {agentPhone && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    AI Agent Phone:{" "}
                    <a href={`tel:${agentPhone}`} className="hover:underline" style={{ color: P }}>{agentPhone}</a>
                  </p>
                )}
                {agentEmail && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    <a href={`mailto:${agentEmail}`} className="hover:underline" style={{ color: P }}>{agentEmail}</a>
                  </p>
                )}
                {companyWebsite && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    <a href={companyWebsite} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: P }}>
                      {companyWebsite.replace(/^https?:\/\//, "")}
                    </a>
                  </p>
                )}
              </div>
            )}

            {/* Professional Summary */}
            {agentBio && (
              <section className="mb-12">
                <MainSectionHeading title="Professional Summary" icon={Briefcase} />
                <div className="prose prose-sm max-w-none text-gray-600
                  prose-p:my-1.5 prose-p:text-sm prose-p:text-gray-600 prose-p:leading-relaxed
                  prose-ul:my-2 prose-ul:pl-5 prose-li:my-0.5 prose-li:text-sm prose-li:text-gray-600
                  prose-ol:my-2 prose-ol:pl-5
                  prose-strong:text-gray-800 prose-strong:font-semibold">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {agentBio.replace(/^[•·]\s*/gm, "- ")}
                  </ReactMarkdown>
                </div>
              </section>
            )}

            {/* My Responsibilities */}
            <section className="mb-12">
              <MainSectionHeading title="My Responsibilities" icon={Briefcase} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {responsibilities.map((role) => (
                  <ExperienceCard key={role.key} role={role} />
                ))}
              </div>
            </section>

            {/* My Tools (main — MCP cards) */}
            {hasMcp && (
              <section className="mb-12">
                <MainSectionHeading title="My Tools" icon={Wrench} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[...mcpTools, ...(teamHandoffTool ? [teamHandoffTool] : [])].map((tool, i) => (
                    <McpCard key={i} tool={tool} />
                  ))}
                </div>
              </section>
            )}

            {/* My Company */}
            {(companyName || locations.length > 0) && (
              <section className="mb-12">
                <MainSectionHeading title="My Company" icon={Building2} />
                {(companyName || companyDescription) && (
                  <div className="mb-6">
                    {companyName && (
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-base" style={{ color: NAVY }}>{companyName}</h3>
                        {companyWebsite && (
                          <a href={companyWebsite} target="_blank" rel="noopener noreferrer" className="text-xs hover:underline" style={{ color: P }}>
                            {companyWebsite.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                      </div>
                    )}
                    {companyDescription && (
                      <p className="text-sm text-gray-500 leading-relaxed">{companyDescription}</p>
                    )}
                  </div>
                )}
                {locations.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {locations.map((loc, i) => (
                      <div key={i} className="rounded-md border border-[#1a2332]/10 overflow-hidden bg-white">
                        {/* Card header */}
                        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#1a2332]/8 bg-[#1a2332]/2">
                          <MapPin className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                          <span className="text-xs font-semibold flex-1" style={{ color: NAVY }}>{loc.name}</span>
                          {loc.isDefault && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">Default</span>
                          )}
                        </div>
                        {/* Card body */}
                        <div className="px-4 py-3 space-y-1.5 text-xs text-gray-500">
                          {loc.address && <p>{loc.address}</p>}
                          {loc.phone && (
                            <a href={`tel:${loc.phone}`} className="flex items-center gap-1.5 hover:text-gray-700">
                              <Phone className="w-3 h-3" />{loc.phone}
                            </a>
                          )}
                          {loc.hours && <p className="text-[10px] text-gray-400">{loc.hours}</p>}
                        </div>
                        {/* Team members */}
                        {(loc.leadMember || (loc.otherMembers && loc.otherMembers.length > 0)) && (
                          <div className="border-t border-[#1a2332]/8 px-4 py-3">
                            {loc.leadMember && (
                              <div className="mb-2">
                                <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1">
                                  Lead Team Member (1)
                                </p>
                                <MemberRow member={loc.leadMember} isLead />
                              </div>
                            )}
                            {loc.otherMembers && loc.otherMembers.length > 0 && (
                              <div>
                                <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1">
                                  Other Team Members ({loc.otherMembers.length})
                                </p>
                                {loc.otherMembers.map((m, j) => (
                                  <MemberRow key={j} member={m} />
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* My Training */}
            {hasTraining && (
              <section className="mb-12">
                <MainSectionHeading title="My Training" icon={GraduationCap} />
                <div className="space-y-6">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">Basic Knowledge Base</p>
                    <div className="space-y-3">
                      {basicTrainingItems.map((item) => (
                    <div key={item.key} className="border border-[#1a2332]/10 rounded-md bg-white overflow-hidden">
                      <button
                        onClick={() => setTrainingOpen((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
                        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-[#1a2332]/[0.03] transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <BookOpen className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm font-semibold" style={{ color: NAVY }}>{item.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                            style={
                              item.meta === "Not set"
                                ? { background: "#fef2f2", color: "#b91c1c" }
                                : { background: "#f3f4f6", color: "#4b5563" }
                            }
                          >
                            {item.meta}
                          </span>
                          <ChevronDown
                            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${trainingOpen[item.key] ? "rotate-180" : ""}`}
                          />
                        </div>
                      </button>
                      {trainingOpen[item.key] && (
                        <div className="border-t border-[#1a2332]/10 px-4 py-3 space-y-2 text-xs text-gray-600">
                          {item.key === "company-profile" && (
                            <>
                              {extendedKnowledge.length > 0 ? (
                                extendedKnowledge.slice(0, 4).map((section, idx) => (
                                  <div key={idx} className="rounded bg-[#1a2332]/[0.03] px-2.5 py-2">
                                    <p className="font-semibold" style={{ color: NAVY }}>{section.heading}</p>
                                    {section.content && (
                                      <p className="text-gray-500 mt-0.5">
                                        {section.content.length > 180 ? `${section.content.slice(0, 180)}...` : section.content}
                                      </p>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <p className="text-gray-500">No company profile content available yet.</p>
                              )}
                            </>
                          )}

                          {item.key === "icp" && (
                            <>
                              {projectSettings?.businessDescription && (
                                <p><span className="font-semibold" style={{ color: NAVY }}>Business Description:</span> {projectSettings.businessDescription}</p>
                              )}
                              {projectSettings?.idealCustomerDescription && (
                                <p><span className="font-semibold" style={{ color: NAVY }}>Ideal Customer:</span> {projectSettings.idealCustomerDescription}</p>
                              )}
                              {projectSettings?.leadScoringAlgorithm && (
                                <p><span className="font-semibold" style={{ color: NAVY }}>Lead Scoring:</span> {projectSettings.leadScoringAlgorithm}</p>
                              )}
                              {projectSettings?.sicCode && (
                                <p><span className="font-semibold" style={{ color: NAVY }}>SIC Code:</span> {projectSettings.sicCode}</p>
                              )}
                              {!projectSettings?.businessDescription &&
                                !projectSettings?.idealCustomerDescription &&
                                !projectSettings?.leadScoringAlgorithm &&
                                !projectSettings?.sicCode && (
                                  <p className="text-gray-500">No ICP details configured yet.</p>
                                )}
                            </>
                          )}

                          {item.key.startsWith("rag-") && (
                            <>
                              {(trainingDocsByType[item.key] ?? []).length > 0 ? (
                                <>
                                  <p className="text-gray-500">
                                    {(trainingDocsByType[item.key] ?? []).length} source
                                    {(trainingDocsByType[item.key] ?? []).length === 1 ? "" : "s"} indexed.
                                  </p>
                                  {(trainingDocsByType[item.key] ?? []).slice(0, 6).map((doc: any) => (
                                    <div key={doc.id} className="rounded bg-[#1a2332]/[0.03] px-2.5 py-1.5">
                                      <p className="font-medium text-gray-700">{doc.title ?? doc.name ?? "Untitled source"}</p>
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <p className="text-gray-500">No sources found for this training type.</p>
                              )}
                            </>
                          )}

                          {item.key === "test-agent-training" && (
                            <p className="text-gray-500">
                              Use Test Agent Training in Settings to validate the model against your current knowledge base.
                            </p>
                          )}

                          {item.key === "summary" && (
                            <div className="grid grid-cols-2 gap-2">
                              <div className="rounded bg-[#1a2332]/[0.03] px-2.5 py-2"><span className="font-semibold" style={{ color: NAVY }}>Total Sources:</span> {knowledgeDocumentsRaw.length}</div>
                              <div className="rounded bg-[#1a2332]/[0.03] px-2.5 py-2"><span className="font-semibold" style={{ color: NAVY }}>Company Profile Sections:</span> {extendedKnowledge.length}</div>
                              <div className="rounded bg-[#1a2332]/[0.03] px-2.5 py-2"><span className="font-semibold" style={{ color: NAVY }}>Website:</span> {websiteCount}</div>
                              <div className="rounded bg-[#1a2332]/[0.03] px-2.5 py-2"><span className="font-semibold" style={{ color: NAVY }}>Files:</span> {filesCount}</div>
                              <div className="rounded bg-[#1a2332]/[0.03] px-2.5 py-2"><span className="font-semibold" style={{ color: NAVY }}>Text:</span> {textCount}</div>
                              <div className="rounded bg-[#1a2332]/[0.03] px-2.5 py-2"><span className="font-semibold" style={{ color: NAVY }}>Q&A:</span> {qaCount}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-2">Advanced Knowledge Base</p>
                    {ragTrainingItems.length > 0 ? (
                      <div className="space-y-3">
                        {ragTrainingItems.map((item) => (
                          <div key={item.key} className="border border-[#1a2332]/10 rounded-md bg-white overflow-hidden">
                            <button
                              onClick={() => setTrainingOpen((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
                              className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-[#1a2332]/[0.03] transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <BookOpen className="w-3.5 h-3.5 text-gray-400" />
                                <span className="text-sm font-semibold" style={{ color: NAVY }}>{item.label}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span
                                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                  style={{ background: "#f3f4f6", color: "#4b5563" }}
                                >
                                  {item.meta}
                                </span>
                                <ChevronDown
                                  className={`w-3.5 h-3.5 text-gray-400 transition-transform ${trainingOpen[item.key] ? "rotate-180" : ""}`}
                                />
                              </div>
                            </button>
                            {trainingOpen[item.key] && (
                              <div className="border-t border-[#1a2332]/10 px-4 py-3 space-y-2 text-xs text-gray-600">
                                {(trainingDocsByType[item.key] ?? []).length > 0 ? (
                                  <>
                                    <p className="text-gray-500">
                                      {(trainingDocsByType[item.key] ?? []).length} source
                                      {(trainingDocsByType[item.key] ?? []).length === 1 ? "" : "s"} indexed.
                                    </p>
                                    {(trainingDocsByType[item.key] ?? []).slice(0, 6).map((doc: any) => (
                                      <div key={doc.id} className="rounded bg-[#1a2332]/[0.03] px-2.5 py-1.5">
                                        <p className="font-medium text-gray-700">{doc.title ?? doc.name ?? "Untitled source"}</p>
                                      </div>
                                    ))}
                                  </>
                                ) : (
                                  <p className="text-gray-500">No sources found for this training type.</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="border border-dashed border-[#1a2332]/20 rounded-md bg-[#1a2332]/[0.02] px-4 py-3 text-xs text-gray-500">
                        No RAG training content available yet.
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Footer */}
            <footer className="pt-8 border-t border-[#1a2332]/8 text-center">
              <p className="text-[10px] text-gray-400">
                {brandingEnabled && poweredByName ? (
                  <>
                    Powered by{" "}
                    <a
                      href={poweredByUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                      style={{ color: P }}
                    >
                      {poweredByName}
                    </a>
                  </>
                ) : (
                  <>
                    Powered by{" "}
                    <a href="https://www.easygpt.builders/" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: P }}>
                      Inbound GPT
                    </a>
                  </>
                )}
              </p>
            </footer>

          </div>
        </main>
      </div>
      {!isProfileEmbed && channelFlags.liveChat ? (
        <BuilderWjsPreview
          projectId={projectId}
          enabled
          reloadKey={widgetReloadKey}
          builderPreview={embedBuilder}
        />
      ) : null}
    </>
  );
}
